import React, { useState, useEffect } from 'react';
import { FolderTree, Plus, Users, Loader2, Edit2, Trash2, ChevronRight, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import SubcaseMembers from './SubcaseMembers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SubcasesPanel - World Class Minimalist Design
 */
const SubcasesPanel = ({ caseId, isLead }: { caseId: string; isLead?: boolean }) => {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  const t = (en: string, no: string) => isEnglish ? en : no;
  
  const [subcases, setSubcases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedSubcase, setSelectedSubcase] = useState(null);
  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    status: 'ACTIVE'
  });

  useEffect(() => {
    if (caseId) {
      fetchSubcases();
    }
  }, [caseId]);

  const fetchSubcases = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/cases/${caseId}/subcases`);
      setSubcases(response.data.subcases || []);
    } catch (error) {
      console.error('Failed to fetch subcases:', error);
      toast.error(t('Could not load subcases', 'Kunne ikke laste undersaker'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!createForm.name.trim()) {
      toast.error(t('Name is required', 'Navn er påkrevd'));
      return;
    }

    try {
      await axios.post(`${API}/cases/${caseId}/subcases`, createForm);
      toast.success(t('Subcase created', 'Undersak opprettet'));
      setShowCreateModal(false);
      setCreateForm({ name: '', description: '', status: 'ACTIVE' });
      fetchSubcases();
    } catch (error) {
      console.error('Failed to create subcase:', error);
      toast.error(error.response?.data?.detail || t('Could not create subcase', 'Kunne ikke opprette undersak'));
    }
  };

  const handleDelete = async (subcaseId, subcaseName) => {
    if (!window.confirm(t(`Are you sure you want to delete the subcase "${subcaseName}"?`, `Er du sikker på at du vil slette undersaken "${subcaseName}"?`))) {
      return;
    }

    try {
      await axios.delete(`${API}/cases/${caseId}/subcases/${subcaseId}`);
      toast.success(t('Subcase deleted', 'Undersak slettet'));
      
      if (selectedSubcase?.id === subcaseId) {
        setSelectedSubcase(null);
      }
      
      fetchSubcases();
    } catch (error) {
      console.error('Failed to delete subcase:', error);
      toast.error(error.response?.data?.detail || t('Could not delete subcase', 'Kunne ikke slette undersak'));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-emerald-100 text-emerald-700';
      case 'COMPLETED':
        return 'bg-gray-100 text-gray-600';
      case 'ARCHIVED':
        return 'bg-gray-50 text-gray-500';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'ACTIVE':
        return t('Active', 'Aktiv');
      case 'COMPLETED':
        return t('Completed', 'Fullført');
      case 'ARCHIVED':
        return t('Archived', 'Arkivert');
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="min-h-full bg-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  const activeCount = subcases.filter(s => s.status === 'ACTIVE').length;
  const completedCount = subcases.filter(s => s.status === 'COMPLETED').length;

  return (
    <div className="min-h-full bg-white">
      <div className="max-w-6xl mx-auto px-8 py-10">
        
        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">{t('Subcases', 'Undersaker')}</h1>
              <p className="text-sm text-gray-500 mt-1">
                {t('Break down the case into smaller parts with separate teams', 'Del opp saken i mindre deler med egne team')}
              </p>
            </div>
            {isLead && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded-xl transition-colors"
              >
                <Plus className="w-4 h-4" />
                {t('New subcase', 'Ny undersak')}
              </button>
            )}
          </div>

          {/* Stats Row */}
          <div className="flex items-center gap-12 py-5 border-y border-gray-100">
            <div>
              <div className="text-2xl font-semibold text-gray-900">{subcases.length}</div>
              <div className="text-xs text-gray-400 mt-0.5">{t('Total', 'Totalt')}</div>
            </div>
            <div>
              <div className="text-2xl font-semibold text-emerald-600">{activeCount}</div>
              <div className="text-xs text-gray-400 mt-0.5">{t('Active', 'Aktive')}</div>
            </div>
            <div>
              <div className="text-2xl font-semibold text-gray-400">{completedCount}</div>
              <div className="text-xs text-gray-400 mt-0.5">{t('Completed', 'Fullført')}</div>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="grid grid-cols-3 gap-8">
          {/* Left: Subcase List */}
          <div className="col-span-1 space-y-0">
            {subcases.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <FolderTree className="w-6 h-6 text-gray-400" />
                </div>
                <p className="text-gray-900 font-medium mb-1">{t('No subcases yet', 'Ingen undersaker ennå')}</p>
                <p className="text-sm text-gray-500 mb-4">
                  {isLead ? t('Create a subcase to divide the work', 'Opprett en undersak for å dele opp arbeidet') : t('Contact the case lead', 'Kontakt saksansvarlig')}
                </p>
                {isLead && (
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="text-sm text-gray-900 font-medium hover:underline inline-flex items-center gap-1"
                  >
                    {t('Create first subcase', 'Opprett første undersak')} <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ) : (
              subcases.map((subcase) => (
                <div
                  key={subcase.id}
                  onClick={() => setSelectedSubcase(subcase)}
                  className={`group flex items-center gap-4 py-3 cursor-pointer transition-colors border-b border-gray-50 last:border-b-0 ${
                    selectedSubcase?.id === subcase.id ? 'bg-violet-50' : 'hover:bg-gray-50'
                  } -mx-2 px-2 rounded-lg`}
                >
                  {/* Status indicator */}
                  <div className={`w-0.5 h-8 rounded-full flex-shrink-0 ${
                    subcase.status === 'ACTIVE' ? 'bg-emerald-400' :
                    subcase.status === 'COMPLETED' ? 'bg-gray-300' :
                    'bg-gray-200'
                  }`} />
                  
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm text-gray-900 truncate">
                      {subcase.name}
                    </h4>
                    {subcase.description && (
                      <p className="text-xs text-gray-400 truncate mt-0.5">{subcase.description}</p>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="flex items-center gap-1 text-xs text-gray-400">
                      <Users className="w-3 h-3" />
                      {subcase.member_count}
                    </span>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(subcase.status)}`}>
                      {getStatusLabel(subcase.status)}
                    </span>
                  </div>
                  
                  <ChevronRight className={`w-4 h-4 transition-colors ${
                    selectedSubcase?.id === subcase.id ? 'text-violet-400' : 'text-gray-200 group-hover:text-gray-400'
                  }`} />
                </div>
              ))
            )}
          </div>

          {/* Right: Subcase Details */}
          <div className="col-span-2">
            {selectedSubcase ? (
              <div className="space-y-6">
                {/* Subcase Header */}
                <div className="bg-gray-50 rounded-2xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        {selectedSubcase.name}
                      </h2>
                      {selectedSubcase.description && (
                        <p className="text-sm text-gray-500 mt-1">
                          {selectedSubcase.description}
                        </p>
                      )}
                    </div>
                    {isLead && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toast(t('Editing coming soon', 'Redigering kommer snart'), { icon: '⏳' })}
                          className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                          <Edit2 className="w-4 h-4 text-gray-500" />
                        </button>
                        <button
                          onClick={() => handleDelete(selectedSubcase.id, selectedSubcase.name)}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4 text-gray-500 hover:text-red-500" />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${getStatusColor(selectedSubcase.status)}`}>
                      {getStatusLabel(selectedSubcase.status)}
                    </span>
                    <span className="text-xs text-gray-400">
                      {t('Created', 'Opprettet')} {new Date(selectedSubcase.created_at).toLocaleDateString(isEnglish ? 'en-US' : 'nb-NO')}
                    </span>
                  </div>
                </div>

                {/* Subcase Members */}
                <SubcaseMembers
                  subcaseId={selectedSubcase.id}
                  caseId={caseId}
                  isLead={isLead}
                />
              </div>
            ) : (
              <div className="bg-gray-50 rounded-2xl p-12 text-center">
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <FolderTree className="w-6 h-6 text-gray-400" />
                </div>
                <p className="text-gray-900 font-medium mb-1">{t('Select a subcase', 'Velg en undersak')}</p>
                <p className="text-sm text-gray-500">
                  {t('Click on a subcase to see details and members', 'Klikk på en undersak for å se detaljer og medlemmer')}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* AI Suggestion */}
        {subcases.length > 0 && (
          <div className="mt-8 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-5 text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent" />
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-violet-400" />
                <span className="text-xs font-medium text-gray-400">{t('Lexy suggests', 'Lexy foreslår')}</span>
              </div>
              <p className="text-sm text-gray-300">
                {isEnglish 
                  ? `You have ${activeCount} active subcases. Make sure each team has clear tasks and deadlines.`
                  : `Du har ${activeCount} aktive undersaker. Sørg for at hvert team har tydelige oppgaver og frister.`
                }
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">{t('Create subcase', 'Opprett undersak')}</h3>
              <p className="text-sm text-gray-500 mt-1">
                {t('A subcase is a part of the main case with its own members', 'En undersak er en del av hovedsaken med egne medlemmer')}
              </p>
            </div>

            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('Name', 'Navn')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  placeholder={t('E.g. Document review', 'F.eks. Dokumentgjennomgang')}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 text-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('Description', 'Beskrivelse')}
                </label>
                <textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                  placeholder={t('Brief description...', 'Kort beskrivelse...')}
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 text-sm resize-none"
                />
              </div>
            </form>

            <div className="p-6 border-t border-gray-100 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowCreateModal(false);
                  setCreateForm({ name: '', description: '', status: 'ACTIVE' });
                }}
                className="px-4 py-2.5 text-gray-700 hover:bg-gray-50 rounded-xl transition-colors text-sm font-medium"
              >
                {t('Cancel', 'Avbryt')}
              </button>
              <button
                onClick={handleCreate}
                className="px-4 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors text-sm font-medium"
              >
                {t('Create subcase', 'Opprett undersak')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubcasesPanel;
